import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import ReaderReviews from "@/components/ReaderReviews";
import { getStoredAttribution } from "@/attribution";
import { POSTAL_ADDRESS } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEAL = "#69d2c7";
const GREEN = "#22c55e";

// Add real testimonial screenshots here: { src: "/testimonials/xyz.png", alt: "Reader message" }
const TESTIMONIAL_IMAGES = [];

function EmailForm({ idPrefix }) {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Please enter your email.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const src = new URLSearchParams(window.location.search).get("src");
      const { first, last } = getStoredAttribution();
      const res = await axios.post(`${API}/free-book`, {
        first_name: firstName.trim(),
        email: email.trim(),
        ...(src ? { src } : {}),
        ...(first ? { attribution_first: first } : {}),
        ...(last ? { attribution_last: last } : {}),
      });
      try {
        sessionStorage.setItem("bt_lead_email", email.trim());
        sessionStorage.setItem("bt_download_token", res.data.download_token || "");
      } catch {}
      navigate("/thank-you");
    } catch {
      setError("Something went wrong. Please check your details and try again.");
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md lg:max-w-[640px] mx-auto border border-neutral-200 bg-white p-6 sm:p-8 lg:px-10 lg:py-9" data-testid={`${idPrefix}-form-card`}>
      <div className="text-center mb-5">
        <p className="text-xl font-bold text-neutral-950">The Cage</p>
        <p className="mt-1 text-sm font-bold tracking-[0.22em] uppercase text-neutral-400">The Full Book</p>
      </div>
      <form onSubmit={submit} data-testid={`${idPrefix}-form`} noValidate>
        <div>
          <label htmlFor={`${idPrefix}-first-name`} className="block text-left text-sm font-semibold mb-1.5">
            Name (optional)
          </label>
          <input
            id={`${idPrefix}-first-name`}
            type="text"
            autoComplete="name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
            data-testid={`${idPrefix}-first-name-input`}
            className="w-full border border-neutral-300 focus:border-neutral-900 outline-none text-base px-4 py-3 placeholder:text-neutral-400"
          />
        </div>
        <div className="mt-3">
          <label htmlFor={`${idPrefix}-email`} className="block text-left text-sm font-semibold mb-1.5">
            Email
          </label>
          <input
            id={`${idPrefix}-email`}
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            data-testid={`${idPrefix}-email-input`}
            className="w-full border border-neutral-300 focus:border-neutral-900 outline-none text-base px-4 py-3 placeholder:text-neutral-400"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid={`${idPrefix}-submit-button`}
          className="mt-5 w-full text-lg font-extrabold py-4 px-8 bg-neutral-950 text-white hover:bg-neutral-800 disabled:opacity-60"
        >
          {loading ? "Sending…" : "Send me the FREE book"}
        </button>
        {error && (
          <p className="mt-3 text-sm text-red-600 text-left" data-testid={`${idPrefix}-error`} role="alert">{error}</p>
        )}
        <p className="mt-4 text-sm text-neutral-500 text-center" data-testid={`${idPrefix}-reassurance`}>
          Instant delivery&nbsp;·&nbsp;Occasional updates
        </p>
        <p className="mt-1 text-sm text-center" style={{ color: "#16a34a" }} data-testid={`${idPrefix}-reassurance-2`}>
          Nothing hidden. You fill in the form, you get the book.
        </p>
      </form>
    </div>
  );
}

const BULLETS = [
  "Why you keep trading while you watch yourself lose",
  "The 8 stages of a spiral, ranked by what each one costs you",
  "What breaks discipline under pressure, and the structure that replaces it",
  "The 5-question scorecard for the weeks when P&L lies",
  "How blowing your account becomes structurally impossible",
  "Why your worst trades are decided before the market opens",
];

export default function Landing() {
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white text-neutral-950" style={{ fontFamily: "'Inter Tight', ui-sans-serif, system-ui, sans-serif" }} data-testid="page-landing">
      <main>
        {/* ANNOUNCEMENT BAR — full width */}
        <div className="w-full bg-black border-y border-red-900/70" data-testid="landing-announcement-bar">
          <div className="max-w-[1150px] mx-auto px-5 md:px-8 py-2.5 md:py-3 flex items-center justify-center gap-2.5">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0" aria-hidden="true" data-testid="landing-announcement-dot" />
            <p className="text-white text-[10px] md:text-xs font-bold tracking-[0.12em] uppercase text-center leading-relaxed" style={{ textWrap: "balance" }}>
              Live trading · September 15 · 9:00 AM New York time: real trades, heart-rate overlay, no hindsight
            </p>
          </div>
        </div>

        {/* 1. HERO */}
        <section className="max-w-screen-md lg:max-w-[1100px] mx-auto px-5 md:px-8 pt-8 md:pt-10 pb-12 md:pb-16 text-center" data-testid="landing-hero">
          <p className="text-xs font-bold tracking-[0.22em] uppercase" data-testid="landing-label">
            <span className="inline-block px-2 py-1 text-white" style={{ backgroundColor: GREEN }}>Free Book</span>
            <span className="text-neutral-400 whitespace-nowrap"> by The Biology Trader</span>
          </p>
          <h1 className="mt-6 text-[clamp(1.3rem,6.6vw,1.62rem)] sm:text-4xl md:text-[clamp(2.2rem,4.25vw,2.95rem)] font-bold max-w-3xl lg:max-w-[1050px] mx-auto" style={{ lineHeight: 1.07, letterSpacing: "-0.02em" }} data-testid="landing-headline">
            <span className="block">You know what to do.</span>
            <span className="block mt-2" style={{ textWrap: "balance" }}>Under pressure, you become <span className="underline decoration-2 underline-offset-4" style={{ textDecorationColor: TEAL }}>someone who can’t do it.</span></span>
          </h1>
          <p className="mt-5 text-base sm:text-lg leading-relaxed text-neutral-600 max-w-xl lg:max-w-[800px] mx-auto" style={{ textWrap: "balance" }} data-testid="landing-subheadline">
            A book of 125 pages, drawn from nine years of trading and 168{"\u00A0"}timestamped journal entries. Why experienced traders still destroy what they’ve built, and <strong className="text-neutral-950 font-semibold">what stops it</strong>.
          </p>
          <p className="mt-3 text-sm sm:text-base font-semibold text-neutral-950 text-center" data-testid="landing-free-reinforce">
            <span className="block">Written by an active futures trader.<br className="min-[600px]:hidden" /> Consistently profitable since 2024.</span>
            <span className="block mt-1.5">I would rather ten thousand traders read it<br className="min-[600px]:hidden" /> than three hundred buy it.</span>
          </p>

          {/* 2. FIRST FORM */}
          <div className="mt-8">
            <EmailForm idPrefix="landing-hero" />
          </div>
        </section>

        {/* 3. INSIDE THE EBOOK */}
        <section className="border-t border-neutral-200" data-testid="landing-curiosity">
          <div
            className="px-5 md:px-6 py-12 md:py-16"
            style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
          >
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-center">
              Inside the <span style={{ color: GREEN }}>FREE</span> book
            </h2>
            <div className="mt-8 md:mt-10 flex flex-col md:flex-row md:items-start md:justify-center md:gap-12 lg:gap-16 w-full">
              <div className="flex flex-col items-center shrink-0">
                <img
                  src="/the-cage-cover.webp"
                  alt="THE CAGE book cover"
                  className="block mx-auto w-[190px] md:w-[219px] lg:w-[230px] h-auto"
                  style={{ height: "auto", maxWidth: "calc(100vw - 48px)" }}
                  width="1333"
                  height="2000"
                  data-testid="landing-book-cover"
                />
                <p className="mt-4 text-sm text-neutral-500 text-center" data-testid="landing-book-cover-caption">
                  PDF&nbsp;&nbsp;•&nbsp;&nbsp;125 pages
                </p>
              </div>
              <ul
                className="mt-8 md:mt-4 mx-auto md:mx-0"
                style={{
                  width: "520px",
                  maxWidth: "calc(100vw - 48px)",
                }}
                data-testid="landing-bullets"
              >
                {BULLETS.map((b, i) => (
                  <li
                    key={i}
                    className={i === 0 ? "" : "mt-5"}
                    style={{ display: "grid", gridTemplateColumns: "30px minmax(0, 1fr)", columnGap: "16px", alignItems: "start" }}
                  >
                    <span className="w-7 h-7 flex items-center justify-center text-sm font-bold text-white rounded-full" style={{ backgroundColor: GREEN }} aria-hidden="true">✓</span>
                    <span className="text-left font-medium text-base sm:text-lg leading-snug pt-0.5">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* TRADING CYCLE */}
        <section className="border-t border-neutral-200" data-testid="landing-trading-cycle">
          <div className="max-w-4xl mx-auto px-5 md:px-8 py-14 md:py-20 text-center">
            <h3 className="section-label" style={{ textAlign: "center" }}>The trading cycle</h3>
            <div className="mt-8 md:mt-10 font-display text-xl sm:text-2xl md:text-3xl lg:text-4xl leading-snug text-ink" data-testid="landing-trading-cycle-line">
              <span className="whitespace-nowrap">Learning <span className="text-muted">→</span></span>{" "}
              <span className="whitespace-nowrap">Improvement <span className="text-muted">→</span></span>{" "}
              <span className="whitespace-nowrap">Confidence <span className="text-muted">→</span></span>
              <br className="hidden md:block" />{" "}
              <span className="whitespace-nowrap">Increased pressure <span className="text-muted">→</span></span>{" "}
              <span className="whitespace-nowrap">Loss of control <span className="text-muted">→</span> <span className="text-red-600">Spiral</span></span>
            </div>
          </div>
        </section>

        {/* 4. QUALIFICATION STATEMENT */}
        <section className="border-t border-neutral-200" data-testid="landing-qualification">
          <div className="max-w-2xl mx-auto px-5 md:px-8 py-14 md:py-20 text-center">
            <p className="text-2xl sm:text-3xl font-bold tracking-tight leading-snug" style={{ textWrap: "balance" }} data-testid="landing-qualification-line-1">
              Your edge is not missing. It is being overridden.
            </p>
            <p className="mt-5 text-lg sm:text-xl leading-relaxed text-neutral-600 max-w-lg mx-auto" style={{ textWrap: "balance" }} data-testid="landing-qualification-line-2">
              This book names the nine patterns that <span className="whitespace-nowrap">override it</span>,
              <br className="hidden md:inline" />
              {" "}explains the mechanism behind each, and
              <br className="hidden md:inline" />
              {" "}shows the structural response each one demands.
            </p>
          </div>
        </section>

        {/* 5. CREDIBILITY */}
        <section className="border-t border-neutral-200" data-testid="landing-credibility">
          <div className="max-w-2xl mx-auto px-5 md:px-8 py-12 md:py-16 text-center">
            <p className="text-base sm:text-lg leading-relaxed text-neutral-600" style={{ textWrap: "pretty" }}>
                Built from nine years of trading experience, 168 timestamped<br className="hidden md:inline" /> journal entries, repeated failures, and the process of discovering<br className="hidden md:inline" /> why biology, not knowledge, was what I had been missing.
            </p>
            <p className="mt-5 text-base sm:text-lg font-semibold" data-testid="landing-credibility-author">
              For traders who already have an edge &amp;<br />
              lose access to it under pressure.<br />
              <span className="underline underline-offset-4">Written for the version of you that takes over<br className="min-[600px]:hidden" /> after two losses.</span>
            </p>
          </div>
        </section>

        {/* 5b. AUTHOR NOTE IMAGE */}
        <section className="border-t border-neutral-200" data-testid="landing-author-note">
          <div className="max-w-2xl mx-auto px-5 md:px-8 py-12 md:py-16 flex justify-center">
            <img
              src="/author-note-landing.png"
              alt="A note from the author"
              className="w-full h-auto"
              style={{ maxWidth: "480px" }}
              loading="lazy"
              data-testid="landing-author-note-image"
            />
          </div>
        </section>

        {/* 6. READER REVIEWS — live data from /api/reviews */}
        <ReaderReviews />

        {/* 7. TESTIMONIAL SCREENSHOTS — hidden until real screenshots are added */}
        {TESTIMONIAL_IMAGES.length > 0 && (
          <section className="border-t border-neutral-200" data-testid="landing-testimonials">
            <div className="max-w-screen-lg mx-auto px-5 md:px-8 py-12 md:py-16">
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-center">
                What traders are saying
              </h2>
              <div className="mt-8 md:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-testid="landing-testimonials-grid">
                {TESTIMONIAL_IMAGES.map((img, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setLightbox(img)}
                    className="border border-neutral-200 bg-white p-2 cursor-zoom-in hover:border-neutral-400"
                    data-testid={`landing-testimonial-${i}`}
                    aria-label={`Open testimonial ${i + 1}`}
                  >
                    <img src={img.src} alt={img.alt || "Reader testimonial"} className="w-full h-auto object-contain" loading="lazy" />
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* 8. FINAL CTA */}
        <section className="border-t border-neutral-200" data-testid="landing-final-cta">
          <div className="max-w-screen-md mx-auto px-5 md:px-8 py-12 md:py-16 text-center">
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight">
              One bad day should not decide the account.
            </h2>
            <div className="mt-8">
              <EmailForm idPrefix="landing-bottom" />
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-neutral-200" data-testid="landing-footer">
        <div className="max-w-screen-md mx-auto px-5 md:px-8 py-8 text-sm text-neutral-500">
          <div className="flex items-center justify-between">
            <span>© {new Date().getFullYear()} Biology Trader Education LLC</span>
            <span className="flex items-center gap-5">
              <Link to="/risk-disclosure" target="_blank" rel="noopener noreferrer" className="hover:text-neutral-950 underline underline-offset-4" data-testid="landing-risk-link">
                Risk Disclosure
              </Link>
              <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-neutral-950 underline underline-offset-4" data-testid="landing-privacy-link">
                Privacy Policy
              </Link>
            </span>
          </div>
          <p className="block mt-4 text-xs text-neutral-400" data-testid="landing-postal-address">
            {POSTAL_ADDRESS}
          </p>
          <p className="block mt-4 text-xs text-neutral-400" data-testid="landing-risk-line">
            Trading involves substantial risk. Past performance does not guarantee future results.
          </p>
        </div>
      </footer>

      {/* LIGHTBOX */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
          data-testid="landing-lightbox"
        >
          <img src={lightbox.src} alt={lightbox.alt || "Reader testimonial"} className="max-w-full max-h-full object-contain" />
          <button
            type="button"
            className="absolute top-4 right-4 text-white text-3xl font-bold leading-none p-2"
            onClick={() => setLightbox(null)}
            aria-label="Close"
            data-testid="landing-lightbox-close"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
